import { useMemo } from 'react';

import { useQuery } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';

import { DEFAULT_RADIUS } from 'src/components/InfiniteJobList/useQueryJobsCount';
import { Config } from 'src/types/gojob';

export const JobsIdQuery = gql`
  query JobIdsQuery($text: String, $location: LocationArgs, $take: Int, $config: ConfigArgs) {
    search(text: $text, take: $take, filters: { location: $location }, config: $config) {
      items {
        id
      }
    }
  }
`;

export type JobsIdsQueryResponse = {
  search: {
    items?: {
      id?: string;
    }[];
  };
};

const useQueryJobsIds = (
  query?: string,
  lat?: number,
  lng?: number,
  radius = DEFAULT_RADIUS,
  take = 20,
  searchType: Config['searchType'] = undefined,
  skip = false
) => {
  const location = useMemo(() => {
    if (lat && lng) {
      return { radius, latitude: lat, longitude: lng };
    }
  }, [lat, lng, radius]);

  const config = useMemo(() => {
    if (searchType) {
      return { searchType };
    }
  }, [searchType]);

  const variables = useMemo(() => {
    return { location, config, take, text: query || undefined };
  }, [query, take, config, location]);

  const { data, error, loading } = useQuery<JobsIdsQueryResponse>(JobsIdQuery, {
    variables,
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const ids = useMemo(() => {
    if (error) {
      return [];
    }
    return data?.search?.items?.map((item) => item.id).filter((id) => !!id);
  }, [data, error]);

  return { ids, error, loading };
};

export default useQueryJobsIds;
